// LCP RUM beacon.
//
// Subscribes to web-vitals' onLCP and posts a structured per-page-view payload
// to /rum/lcp via navigator.sendBeacon. Captures the four-phase attribution
// breakdown (TTFB, resource load delay, resource load duration, element render
// delay) plus the LCP element CSS selector, resource URL, navigation type, and
// viewport dimensions. The entity is read from the server-injected RequestContext
// (via requestContext) and sent in the payload — it can't be re-derived from the
// beacon's own POST to /rum/lcp, which loses the page's /online/apps namespace.
// The URL is relative, so the beacon posts same-origin to /rum/lcp on whatever
// host served the page (www, a white-label app, an EXPERIENCE_MAP domain); the
// endpoint answers on every host Rankers serves and records that host.
//
// Skipped inside native-app WKWebViews — that population is measured separately
// from the website's CrUX signal and isn't what GSC's Core Web Vitals report
// rolls up.
//
// See plans/2026-06-04--core-web-vitals-lcp-optimization.md (Phase 0).

import { onLCP } from "web-vitals/attribution";

import { requestContext } from "../request_context";

const BEACON_URL = "/rum/lcp";

function safe(value, fallback) {
    if (value === null || value === undefined || Number.isNaN(value)) return fallback;
    return value;
}

// Exported for test/javascript/unit/rankers/lib/rum/lcp_beacon.test.js.
export function buildPayload(metric) {
    const attribution = metric.attribution || {};
    const navEntry = (performance.getEntriesByType("navigation") || [])[0];

    return {
        value: safe(metric.value, 0),
        rating: metric.rating || "",
        entity: requestContext.entityTypeCode(),
        entityCode: requestContext.entityCode(),
        ttfb: safe(attribution.timeToFirstByte, 0),
        loadDelay: safe(attribution.resourceLoadDelay, 0),
        loadDuration: safe(attribution.resourceLoadDuration, 0),
        renderDelay: safe(attribution.elementRenderDelay, 0),
        selector: attribution.target || "",
        url: attribution.url || "",
        navType: metric.navigationType || (navEntry && navEntry.type) || "",
        viewportW: window.innerWidth || 0,
        viewportH: window.innerHeight || 0,
        dpr: window.devicePixelRatio || 1,
        // The path only: email links carry sign-in and other tokens in the query string, and the
        // beacon is written to a log. The server also strips queries and token path segments
        // (Rum::PathSanitiser). See plans/2026-10-12--keep-sign-in-tokens-out-of-rum-logs.md.
        path: window.location.pathname,
    };
}

export function initLcpBeacon() {
    if (requestContext.isNativeApp()) return;
    if (typeof navigator === "undefined") return;
    if (typeof navigator.sendBeacon !== "function") return;

    onLCP((metric) => {
        try {
            const payload = buildPayload(metric);
            const body = new Blob([JSON.stringify(payload)], { type: "application/json" });
            navigator.sendBeacon(BEACON_URL, body);
        } catch (e) {
            // Beacon ingestion is best-effort; swallow errors.
        }
    });
}
